/*
 * Copyright 2019 Poly Forest, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { Pool } from './Pool'
import { freeTo, ObjectPool } from './Pool'
import { type Clearable, compare, first, last, sortedInsertionIndex } from '@amazon/vinyl-util'

/**
 * A layer between item reuse and an object pool that first seeks an item from the same index.
 * It is to reduce the amount of changes a pooled item may need to make.
 *
 * To use this class, make a series of `obtain` calls to grab recycled elements, then call `flip` to send unused
 * elements back to the object pool and make the obtained elements available for the next series.
 *
 * A set may look like this:
 *
 * ```
 * obtain(3)
 * obtain(4)
 * obtain(5)
 * obtain(2)
 * obtain(1)
 * flip() // 1 through 5 is available in the next set for fast access.
 * ```
 */
export class IndexedPool<E> implements Clearable {
    private current: E[] = []
    private currentIndices: number[] = []

    private obtained: E[] = []
    private obtainedIndices: number[] = []
    private readonly pool: Pool<E>

    constructor(factory: () => E, initialCapacity = 8, capacity = 20000) {
        this.pool = new ObjectPool(factory, initialCapacity, capacity)
    }

    get size(): number {
        return this.current.length
    }

    get obtainedSize(): number {
        return this.obtained.length
    }

    get(index: number): E {
        return this.current[index]
    }

    /**
     * Obtains an element.
     * This algorithm attempts to obtain an element with the same index that was cached in the last obtain/flip set.
     */
    obtain(index: number): E {
        let element: E
        if (this.current.length === 0) {
            // Nothing in the previous set
            element = this.pool.obtain()
        } else {
            // Check if the index is available in the previous set.
            const sortedIndex = sortedInsertionIndex(this.currentIndices, index, compare) - 1
            if (sortedIndex !== -1 && this.currentIndices[sortedIndex] === index) {
                // Matched
                element = this.current.splice(sortedIndex, 1)[0]
                this.currentIndices.splice(sortedIndex, 1)
            } else {
                // No match, take the index furthest from the requested index
                if (
                    Math.abs(first(this.currentIndices)! - index) >
                    Math.abs(last(this.currentIndices)! - index)
                ) {
                    // First index in the set is further from the requested index than the last
                    element = this.current.shift()!
                    this.currentIndices.shift()
                } else {
                    element = this.current.pop()!
                    this.currentIndices.pop()
                }
            }
        }

        const insertionIndex = sortedInsertionIndex(this.obtainedIndices, index, compare)
        this.obtained.splice(insertionIndex, 0, element)
        this.obtainedIndices.splice(insertionIndex, 0, index)
        return element
    }

    /**
     * Iterates over each unused item still in the cache.
     */
    forEachUnused(callback: (index: number, element: E) => void): this {
        for (let i = 0; i < this.current.length; i++) {
            callback(this.currentIndices[i], this.current[i])
        }
        return this
    }

    /**
     * Sets the items returned via [obtain] to be used as the cached items for the next set.
     */
    flip(): void {
        this.current.forEach((e) => this.pool.free(e))

        const tmp = this.current
        const tmpIndices = this.currentIndices

        this.current = this.obtained
        this.currentIndices = this.obtainedIndices

        this.obtained = tmp
        this.obtained.length = 0
        this.obtainedIndices = tmpIndices
        this.obtainedIndices.length = 0
    }

    /**
     * Clears this list, sending all known elements back to the pool.
     */
    clear(): void {
        freeTo(this.current, this.pool)
        freeTo(this.obtained, this.pool)
        this.currentIndices.length = 0
        this.obtainedIndices.length = 0
    }
}
